'use client'

import { useEffect, useRef, useState } from 'react';
import {
    Disclosure, DisclosureButton, DisclosurePanel,
} from '@headlessui/react'
import {
    HomeIcon,
    ClipboardDocumentListIcon,
    ShoppingBagIcon,
    BuildingStorefrontIcon,
    TruckIcon,
    ChartBarIcon,
    UsersIcon,
    UserPlusIcon,
    BookOpenIcon,
} from '@heroicons/react/24/outline'

import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import { useAuth } from "../contexts/AuthContext.tsx";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { UserIcon } from "@heroicons/react/16/solid";


const navigation = [
    { name: 'Overview', href: '/', icon: HomeIcon },

    {
        name: 'Orders & Fulfilment',
        icon: ClipboardDocumentListIcon,
        children: [
            { name: 'Orders', href: '/orders' },
            { name: 'Deliveries', href: '/deliveries' },
            { name: 'Collections', href: '/collections' },
            { name: 'Delivery Dispatch', href: '/delivery-dispatch' },
        ]
    },

    {
        name: 'Catalogue',
        icon: ShoppingBagIcon,
        allowedRoles: ['admin', 'super'],
        children: [
            { name: 'Products', href: '/products' },
            { name: 'Categories', href: '/categories' },
            { name: 'Specials', href: '/specials' },
        ]
    },

    { name: 'Stores', href: '/stores', icon: BuildingStorefrontIcon },

    {
        name: 'Operations',
        icon: TruckIcon,
        children: [
            { name: 'Drivers', href: '/drivers' },
            { name: 'Pickers', href: '/pickers' },
            { name: 'Live Map', href: '/ActiveDeliveries' },
            { name: 'Delivery Options', href: '/delivery-options' },
            { name: 'Collection Points', href: '/collection-points' },
            { name: 'FAQs', href: '/faqs', allowedRoles: ['admin', 'super'] },
            { name: 'Build my Cake', href: '/cake-config' },

        ]
    },

    { name: 'Customers', href: '/customers', icon: UsersIcon },
    { name: 'Analytics', href: '/analytics', icon: ChartBarIcon,   allowedRoles: ['admin', 'super'], },
    { name: 'Handbook', href: '/help', icon: BookOpenIcon },
];

function classNames(...classes: (string | undefined | false)[]) {
    return classes.filter(Boolean).join(' ')
}

export default function AdminLayout() {
    const location = useLocation();
    const { user, signOut, profile } = useAuth();
    const navigate = useNavigate();

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    type NavChild = { name: string; href: string; allowedRoles?: string[] };

    const canSeeNavItem = (item: typeof navigation[number]) => {
        if (!item.allowedRoles?.length) return true;
        return item.allowedRoles.includes(profile?.role ?? '');
    };

    const canSeeChild = (child: NavChild) => {
        if (!child.allowedRoles?.length) return true;
        return child.allowedRoles.includes(profile?.role ?? '');
    };

    const visibleNavigation = navigation.filter(canSeeNavItem);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = async (): Promise<void> => {
        try {
            await signOut();
            localStorage.removeItem('supabase.auth.token');
            navigate('/login', { replace: true });
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const isChildActive = (children: { name: string; href: string }[]) => {
        return children.some((child) => location.pathname === child.href);
    };

    return (
        <div className="min-h-screen bg-stone-50">
            {/* Static Sidebar for Desktop */}
            <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
                <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-stone-200 bg-[#161d2b] px-6 pb-4">
                    <div className="flex h-16 shrink-0 items-center gap-3">
                        <img src={logo} alt="Save Logo" className="h-8 w-auto rounded-md shadow-sm" />
                        <span className="text-xl font-medium tracking-tight text-white">Save Delivery</span>
                    </div>

                    <nav className="flex flex-1 flex-col">
                        <ul role="list" className="flex flex-1 flex-col gap-y-4">
                            <li>
                                <ul role="list" className="-mx-2 space-y-1">
                                    {visibleNavigation.map((item) => (
                                        <li key={item.name}>
                                            {!item.children ? (
                                                <Link
                                                    to={item.href}
                                                    className={classNames(
                                                        location.pathname === item.href
                                                            ? 'bg-[#333c45] text-white'
                                                            : 'text-gray-50 hover:bg-[#333c45] hover:text-white',
                                                        'group flex gap-x-3 rounded-md p-2 text-sm font-medium transition-colors duration-200 items-center'
                                                    )}
                                                >
                                                    <item.icon className="size-6 shrink-0" aria-hidden="true" />
                                                    {item.name}
                                                </Link>
                                            ) : (
                                                <Disclosure as="div" defaultOpen={isChildActive(item.children)}>
                                                    {({ open }) => (
                                                        <>
                                                            <DisclosureButton
                                                                className={classNames(
                                                                    isChildActive(item.children)
                                                                        ? 'text-white'
                                                                        : 'text-white hover:bg-[#333c45] hover:text-white',
                                                                    'group flex w-full items-center gap-x-3 rounded-md p-2 text-left text-sm font-medium transition-colors duration-200'
                                                                )}
                                                            >
                                                                <item.icon className="size-6 shrink-0" aria-hidden="true" />
                                                                <span>{item.name}</span>
                                                                <ChevronDownIcon
                                                                    className={classNames(
                                                                        open ? 'rotate-0 text-white' : '-rotate-90 text-stone-200',
                                                                        'ml-auto size-5 shrink-0 transition-transform duration-200'
                                                                    )}
                                                                    aria-hidden="true"
                                                                />
                                                            </DisclosureButton>

                                                            <DisclosurePanel as="ul" className="mt-1 space-y-1">
                                                                {item.children.filter(canSeeChild).map((subItem) => (
                                                                    <li key={subItem.name}>
                                                                        <Link
                                                                            to={subItem.href}
                                                                            className={classNames(
                                                                                location.pathname === subItem.href
                                                                                    ? 'bg-[#333c45] text-white'
                                                                                    : 'text-white hover:bg-[#333c45] hover:text-white',
                                                                                'block rounded-md py-2 pl-9 pr-2 text-sm  transition-colors duration-200 font-medium mx-2'
                                                                            )}
                                                                        >
                                                                            {subItem.name}
                                                                        </Link>
                                                                    </li>
                                                                ))}
                                                            </DisclosurePanel>
                                                        </>
                                                    )}
                                                </Disclosure>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </li>
                            {user && (profile?.role === 'admin' || profile?.role === 'super') && (
                            <li className="mt-auto">
                                <button
                                    onClick={() => navigate('/create-user')}
                                    className="group -mx-2 flex w-full gap-x-3 rounded-xl bg-[#333c45] border border-stone-200 p-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#3d4752] hover:border-white transition-all duration-200 items-center justify-center cursor-pointer"
                                >
                                    <UserPlusIcon className="size-5 shrink-0 text-white" aria-hidden="true" />
                                    Create User
                                </button>
                                <p className="text-center text-xs font-medium text-white/15 mt-2">
                                    Version v1.0.0
                                </p>
                            </li>
                            )}
                        </ul>
                    </nav>
                </div>
            </div>

            {/* Right Side Content */}
            <div className="lg:pl-72">
                {/* Top Header */}
                <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-stone-200 bg-white px-4 shadow-xs sm:gap-x-6 sm:px-6 lg:px-8 print:hidden">
                    <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6 justify-end items-center">
                        {user ? (
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    className="flex items-center gap-2.5 px-3 py-1.5 rounded-full transition-colors cursor-pointer "
                                >
                                    <div className="bg-stone-100 p-1 rounded-full">
                                        <UserIcon className="h-6 w-6 text-[#161d2b]" aria-hidden="true" />
                                    </div>
                                    <span className="text-stone-600 font-medium">
                                        Hi, {profile?.first_name?.split(' ')[0] || 'User'}
                                    </span>
                                    <ChevronDownIcon
                                        className={`h-5 w-5 text-stone-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                                        aria-hidden="true"
                                    />
                                </button>

                                {isDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white border border-stone-200 rounded-xl shadow-lg overflow-hidden z-50 animate-in fade-in slide-in-from-top-1">
                                        <div className="px-4 py-3 bg-stone-50/50 border-b border-stone-200">
                                            <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Signed in as</p>
                                            <p className="text-sm font-medium text-stone-900 truncate">{user.email}</p>
                                        </div>
                                        <div className="p-1">
                                            <button
                                                onClick={handleLogout}
                                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer font-medium"
                                            >
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                                                    />
                                                </svg>
                                                Logout
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Link to="/login" className="text-stone-600 hover:text-stone-900 font-medium cursor-pointer">
                                Login
                            </Link>
                        )}
                    </div>
                </div>

                {/* Main Content Area */}
                <main className="py-8 min-h-[calc(100vh-64px)] print:py-0">
                    <div className="px-4 sm:px-6 lg:px-8 print:px-0">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    )
}